// SPDX-License-Identifier: Apache-2.0

// WikilinkAutocomplete — popover for `[[…]]` references in the modal editor.
//
// Behaviour spec from the gift:
//   • Opens when the user types `[[` in the textarea.
//   • Filters `vault.docs()` by typed prefix (fuzzy on title AND permalink).
//   • Arrow keys navigate; Enter selects; Esc closes.
//   • Inserts the title slug, replacing the `[[query` segment with `[[Title]]`.
//   • If no doc matches, shows "+ create new doc" → calls `vault.createBlank`
//     and inserts the new title's wikilink.
//
// Caret positioning: classic "hidden mirror div" trick. We clone the textarea's
// computed style onto a fixed-position div, write the text up to the caret
// followed by a zero-width marker span, then read `getBoundingClientRect()` on
// the span. That rect (plus a tiny offset) is the absolute popover anchor.
// This is the same approach popularized by `textarea-caret-position` and used
// by Slack/Linear/the OLD Tether's earlier autocomplete pass.

import {
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
  Show,
  For,
} from "solid-js";
import type { VaultDoc } from "@tether/storage";
import type { UseVault } from "./useVault";
import { computeCaretXY, type CaretXY } from "./caret-mirror";

export interface WikilinkAutocompleteProps {
  /** The textarea to track. Must be stable for the lifetime of the popover. */
  textareaRef: () => HTMLTextAreaElement | undefined;
  /** Reactive draft content; tracked so we re-evaluate on every keystroke. */
  value: () => string;
  /** Setter for the draft. Called when we splice in the wikilink. */
  setValue: (next: string) => void;
  vault: UseVault;
}

interface Match {
  doc: VaultDoc;
  /** Lower-case display label used for fuzzy matching. */
  haystack: string;
}

/** Drop a doc title to a slug suitable for `[[…]]`. We strip a trailing `.md`
 *  but keep spaces — wikilinks are matched against title (see vaultLinks.ts). */
function titleToWikilink(title: string): string {
  return title.replace(/\.md$/i, "").trim();
}

/** Fuzzy match: every char of `query` must appear in order somewhere in
 *  `haystack`. Returns `null` for no match, otherwise a small score (lower
 *  = better). */
function fuzzyScore(query: string, haystack: string): number | null {
  if (query.length === 0) return 0;
  let qi = 0;
  let lastIdx = -1;
  let gaps = 0;
  for (let i = 0; i < haystack.length && qi < query.length; i++) {
    if (haystack[i] === query[qi]) {
      if (lastIdx >= 0) gaps += i - lastIdx - 1;
      lastIdx = i;
      qi++;
    }
  }
  if (qi < query.length) return null;
  return gaps + (haystack.length - query.length) * 0.01;
}

export function WikilinkAutocomplete(props: WikilinkAutocompleteProps) {
  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal("");
  /** Caret position in `props.value()` where the `[[` opens. We splice from
   *  here on selection. */
  const [openIdx, setOpenIdx] = createSignal(0);
  const [activeIdx, setActiveIdx] = createSignal(0);
  const [pos, setPos] = createSignal<CaretXY>({ left: 0, top: 0 });

  // Recompute matches reactively. Cap at 12 to keep the popover compact.
  const matches = createMemo<Match[]>(() => {
    if (!open()) return [];
    const q = query().toLowerCase();
    const docs = props.vault.docs();
    const scored: Array<{ m: Match; s: number }> = [];
    for (const doc of docs) {
      const title = doc.title.toLowerCase();
      const permalink = String(doc.front?.permalink ?? "").toLowerCase();
      const sTitle = fuzzyScore(q, title);
      const sPerm = permalink ? fuzzyScore(q, permalink) : null;
      const s =
        sTitle !== null && sPerm !== null
          ? Math.min(sTitle, sPerm)
          : sTitle ?? sPerm;
      if (s === null) continue;
      scored.push({ m: { doc, haystack: title }, s });
    }
    scored.sort((a, b) => a.s - b.s);
    return scored.slice(0, 12).map((x) => x.m);
  });

  // Whenever matches change (keystroke), reset the highlighted index.
  createEffect(() => {
    matches();
    setActiveIdx(0);
  });

  function close(): void {
    setOpen(false);
    setQuery("");
  }

  function insertWikilink(title: string): void {
    const ta = props.textareaRef();
    if (!ta) return;
    const v = props.value();
    const start = openIdx();
    // The current segment that we replace is `[[` + the typed query.
    // We're given `openIdx` = position of the first `[`.
    const end = ta.selectionStart;
    const before = v.slice(0, start);
    const after = v.slice(end);
    const slug = titleToWikilink(title);
    const inserted = `[[${slug}]]`;
    const next = before + inserted + after;
    props.setValue(next);
    // Restore caret AFTER the closing `]]`.
    const caret = before.length + inserted.length;
    queueMicrotask(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
    close();
  }

  async function createAndInsert(title: string): Promise<void> {
    if (!title) return;
    // Insert wikilink immediately (so the editor has the text instantly), then
    // mint the blank doc in the background. The user can navigate to it later.
    insertWikilink(title);
    try {
      await props.vault.createBlank(`${title}.md`);
    } catch {
      /* swallow — the wikilink is inserted regardless */
    }
  }

  /** Re-scan the textarea to decide if we should open (or update query, or
   *  close). Called from the parent's `onInput`/`onKeyUp` via a public
   *  `evaluate()` method, but we also wire it ourselves so the popover stays
   *  consistent across cursor moves. */
  function evaluate(): void {
    const ta = props.textareaRef();
    if (!ta) {
      if (open()) close();
      return;
    }
    const caret = ta.selectionStart;
    const v = ta.value;
    // Walk backwards from caret to find a `[[` BEFORE any newline or `]]`.
    let i = caret - 1;
    let openFound = -1;
    while (i >= 1) {
      const c = v[i];
      if (c === "\n") break;
      if (c === "]" && v[i - 1] === "]") {
        // Already-closed wikilink — bail.
        break;
      }
      if (c === "[" && v[i - 1] === "[") {
        openFound = i - 1;
        break;
      }
      i--;
    }

    if (openFound < 0) {
      if (open()) close();
      return;
    }

    const q = v.slice(openFound + 2, caret);
    // Bail if the user typed `]` mid-query — they're closing manually.
    if (q.includes("]") || q.includes("[")) {
      if (open()) close();
      return;
    }
    // Bail if the query is super long — that's almost certainly not what they
    // want to autocomplete.
    if (q.length > 80) {
      if (open()) close();
      return;
    }

    setOpenIdx(openFound);
    setQuery(q);
    setOpen(true);
    // Position the popover at the caret. We anchor on the position of the
    // closing-end of the query (current caret), which feels like the typing
    // position to the user.
    const xy = computeCaretXY(ta, caret);
    setPos(xy);
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (!open()) return;
    const opts = matches();
    const showCreate = query().length > 0 && opts.length === 0;
    const total = opts.length + (showCreate ? 1 : 0);
    if (total === 0) {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % total);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + total) % total);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const i = activeIdx();
      if (i < opts.length) {
        const m = opts[i];
        if (m) insertWikilink(m.doc.title);
      } else if (showCreate) {
        void createAndInsert(query());
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  }

  // Hook input/keyup/click on the textarea so we evaluate on every change.
  createEffect(() => {
    const ta = props.textareaRef();
    if (!ta) return;
    const onInput = () => evaluate();
    const onClick = () => evaluate();
    const onSelChange = () => evaluate();
    ta.addEventListener("input", onInput);
    ta.addEventListener("click", onClick);
    ta.addEventListener("keyup", onSelChange);
    ta.addEventListener("keydown", onKeyDown, true);
    onCleanup(() => {
      ta.removeEventListener("input", onInput);
      ta.removeEventListener("click", onClick);
      ta.removeEventListener("keyup", onSelChange);
      ta.removeEventListener("keydown", onKeyDown, true);
    });
  });

  // Re-evaluate whenever the value mutates from outside (e.g. our own splice).
  createEffect(() => {
    props.value();
    if (open()) evaluate();
  });

  return (
    <Show when={open()}>
      <div
        class="mv-ac-popover"
        data-testid="wikilink-autocomplete"
        data-empty={matches().length === 0 ? "true" : "false"}
        style={{ left: `${pos().left}px`, top: `${pos().top}px` }}
        onMouseDown={(e) => e.preventDefault() /* keep textarea focus */}
      >
        <For each={matches()}>
          {(m, i) => (
            <div
              class="mv-ac-item"
              data-active={i() === activeIdx() ? "true" : "false"}
              onMouseEnter={() => setActiveIdx(i())}
              onClick={() => insertWikilink(m.doc.title)}
            >
              <span class="mv-ac-item__title">{m.doc.title}</span>
              <Show when={m.doc.front?.permalink}>
                <span class="mv-ac-item__hint">
                  {String(m.doc.front?.permalink ?? "")}
                </span>
              </Show>
            </div>
          )}
        </For>
        <Show when={query().length === 0 && matches().length === 0}>
          <div class="mv-ac-item mv-ac-item--empty">type to filter…</div>
        </Show>
        <Show when={query().length > 0 && matches().length === 0}>
          <div
            class="mv-ac-item mv-ac-item--create"
            data-active={activeIdx() === 0 ? "true" : "false"}
            onClick={() => void createAndInsert(query())}
          >
            <span class="mv-ac-item__title">+ create new doc “{query()}”</span>
          </div>
        </Show>
      </div>
    </Show>
  );
}
